import { Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { useStatisticsContext } from 'src/shared/contexts/statisticsContext';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { useGlobals } from 'src/shared/contexts/appContext';
import { StatisticsFilterAutocompleteDatePicker } from '../components/statisticsFilterAutocompleteDatePicker';
import { useMemberGrowth } from '../hooks/useFetchMemberGrowth';
import { StatisticsMemberGrowthDataGrid } from '../components/statisticsMemberGrowthDataGrid';
import { StatisticsMemberGrowthSkeleton } from '../components/statisticsMemberGrowthSkeleton';

export const StatisticsMemberGrowthListPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const { association, setAssociation, district, setDistrict } = useStatisticsContext();
  const { searchFilter } = useStatisticsContext();
  const { memberGrowth, isLoading } = useMemberGrowth(searchFilter);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs>
          <MainContentHeader title={t('statistics:memberProgress.title')} />
        </Grid>
        <Grid
          item
          xs='auto'
          sx={{
            pt: 2,
            pl: 2,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            '& .MuiAutocomplete-inputRoot': {
              backgroundColor: 'white',
            },
          }}
        >
          {currentUser && (
            <AssociationAndDistrictSelect
              hideDistrict={currentUser?.districts.length === 0}
              association={association}
              district={district}
              associationCallback={(val) => setAssociation(val || undefined)}
              districtCallback={(val) => setDistrict(val || undefined)}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{}}>
            <PageFilterSection
              leftContent={
                <Grid container spacing={2}>
                  <StatisticsFilterAutocompleteDatePicker />
                </Grid>
              }
            />
            {isLoading && (
              <>
                <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                  <StatisticsMemberGrowthSkeleton />
                </Grid>
                <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                  <StatisticsMemberGrowthSkeleton />
                </Grid>
                <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                  <StatisticsMemberGrowthSkeleton />
                </Grid>
              </>
            )}
            {!isLoading && memberGrowth && (
              <>
                {memberGrowth.enterCount > 0 && (
                  <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                    <ContentSection header={t('statistics:memberProgress.labels.new')} headerCounter={memberGrowth.enterCount} showHeaderCounter>
                      <StatisticsMemberGrowthDataGrid isNewMembers members={memberGrowth.enterMembers} />
                    </ContentSection>
                  </Grid>
                )}
                {memberGrowth.reenterCount > 0 && (
                  <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                    <ContentSection
                      header={t('statistics:memberProgress.labels.reRegister')}
                      headerCounter={memberGrowth.reenterCount}
                      showHeaderCounter
                    >
                      <StatisticsMemberGrowthDataGrid isNewMembers members={memberGrowth.reenterMembers} />
                    </ContentSection>
                  </Grid>
                )}
                {memberGrowth.exitCount > 0 && (
                  <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                    <ContentSection
                      header={t('statistics:memberProgress.labels.deRegistered')}
                      headerCounter={memberGrowth.exitCount}
                      showHeaderCounter
                    >
                      <StatisticsMemberGrowthDataGrid isNewMembers={false} members={memberGrowth.exitMembers} disableMemberProfileLink />
                    </ContentSection>
                  </Grid>
                )}
                {memberGrowth.deceasedCount > 0 && (
                  <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                    <ContentSection
                      header={t('statistics:memberProgress.labels.deceased')}
                      headerCounter={memberGrowth.deceasedCount}
                      showHeaderCounter
                    >
                      <StatisticsMemberGrowthDataGrid isNewMembers={false} members={memberGrowth.deceasedMembers} />
                    </ContentSection>
                  </Grid>
                )}
                {memberGrowth.movedInCount > 0 && (
                  <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                    <ContentSection
                      header={t('statistics:memberProgress.labels.movedIn')}
                      headerCounter={memberGrowth.movedInCount}
                      showHeaderCounter
                    >
                      <StatisticsMemberGrowthDataGrid isNewMembers members={memberGrowth.movedInMembers} />
                    </ContentSection>
                  </Grid>
                )}
                {memberGrowth.movedOutCount > 0 && (
                  <Grid item xs={12} p={{ xs: 2, md: 4 }}>
                    <ContentSection
                      header={t('statistics:memberProgress.labels.movedOut')}
                      headerCounter={memberGrowth.movedOutCount}
                      showHeaderCounter
                    >
                      <StatisticsMemberGrowthDataGrid isNewMembers={false} members={memberGrowth.movedOutMembers} />
                    </ContentSection>
                  </Grid>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};
